'use client';

import { cn } from '@/utils/utils';
import * as AccordionPrimitive from '@radix-ui/react-accordion';
import {
  ChevronDown,
  ChevronRight,
  type LucideIcon,
  Minus,
  Plus,
} from 'lucide-react';
import * as React from 'react';

const Accordion = AccordionPrimitive.Root;

const AccordionItem = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Item>
>(({ className, ...props }, ref) => (
  <AccordionPrimitive.Item ref={ref} className={cn('', className)} {...props} />
));
AccordionItem.displayName = 'AccordionItem';

interface AccordionTriggerProps
  extends React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Trigger> {
  expandIcon?: LucideIcon;
  collapseIcon?: LucideIcon;
  variant?: 'default' | 'chevron';
}

const AccordionTrigger = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Trigger>,
  AccordionTriggerProps
>(
  (
    {
      className,
      children,
      expandIcon,
      collapseIcon,
      variant = 'default',
      ...props
    },
    ref,
  ) => {
    const ExpandIcon =
      expandIcon || (variant === 'chevron' ? ChevronRight : Plus);
    const CollapseIcon =
      collapseIcon || (variant === 'chevron' ? ChevronDown : Minus);

    return (
      <AccordionPrimitive.Header className="flex">
        <AccordionPrimitive.Trigger
          ref={ref}
          className={cn(
            'group flex items-center text-start w-full gap-2 py-2 px-3 font-medium transition-all rounded-lg',
            variant === 'default' && 'justify-between',
            className,
          )}
          {...props}
        >
          <div className="flex items-center gap-1.5">
            <ExpandIcon
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=open]:hidden',
                variant === 'chevron' && 'h-3 w-3',
              )}
              {...(variant === 'chevron' && { strokeWidth: 3 })}
            />
            <CollapseIcon
              className={cn(
                'h-4 w-4 shrink-0 transition-transform duration-200 group-data-[state=closed]:hidden',
                variant === 'chevron' && 'h-3 w-3',
              )}
              {...(variant === 'chevron' && { strokeWidth: 3 })}
            />
            {children}
          </div>
        </AccordionPrimitive.Trigger>
      </AccordionPrimitive.Header>
    );
  },
);
AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName;

const AccordionContent = React.forwardRef<
  React.ElementRef<typeof AccordionPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof AccordionPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <AccordionPrimitive.Content
    ref={ref}
    className="overflow-hidden text-sm transition-all data-[state=closed]:animate-accordion-up data-[state=open]:animate-accordion-down"
    {...props}
  >
    <div className={cn('py-4 px-3 whitespace-pre-wrap', className)}>
      {children}
    </div>
  </AccordionPrimitive.Content>
));

AccordionContent.displayName = AccordionPrimitive.Content.displayName;

export { Accordion, AccordionItem, AccordionTrigger, AccordionContent };
